<script lang="ts">
	// Brand logo tiles for the curated capability set. OFFICIAL marks for the
	// integrations that have one (Amazon, Asana, Otto, JTL, Plytix, Krea — sourced
	// from Simple Icons / Wikimedia / the brands' own sites); calm neutral
	// lucide-style glyphs for the internal data/skill capabilities that don't.
	// `brand` comes from capability-presentation.ts; null/unknown → a neutral
	// server glyph so personal caps still render something sensible.
	// `mono` forces a quiet, monochrome treatment (used for Skills): a neutral
	// glass tile + the lucide glyph only — NEVER a brand mark — so a skill can
	// never be mistaken for a branded tool, even if its presentation brand is
	// a branded key.
	let { brand = null, size = 36, mono = false }: { brand?: string | null; size?: number; mono?: boolean } =
		$props();

	const tileBg = (b: string | null): string => {
		if (mono) return 'var(--glass)';
		switch (b) {
			case 'jtl':
				return '#212d36';
			case 'krea':
				return '#0a0a0a';
			case 'amazon':
			case 'asana':
			case 'otto':
			case 'plytix':
				return '#15181f';
			case 'kaufland':
				return '#E10915';
			case 'allegro':
				return '#FF5A00';
			// Official brand marks → render white on the brand's own tile colour.
			case 'shopify':
				return '#95BF47';
			case 'zendesk':
				return '#03363D';
			case 'confluence':
				return '#172B4D';
			case 'linear':
				return '#5E6AD2';
			case 'posthog':
				return '#F54E00';
			case 'typeform':
				return '#262627';
			case 'slack':
				return '#4A154B';
			case 'google':
				return '#fff';
			case 'miro':
				return '#050038';
			case 'figma':
				return '#1e1e1e';
			case 'bidx':
				return '#101828';
			case 'home24':
			case 'xydc':
			case 'fireflies':
				return '#f7f7fb';
			case 'klaviyo':
				return '#1b1b1b';
			default:
				return 'var(--accent)';
		}
	};
	// jtl + plytix marks include their own coloured square → render near-full-bleed;
	// the rest are symbols that sit centered with breathing room.
	const isFull = $derived(brand === 'jtl' || brand === 'plytix');
	const mark = $derived(Math.round(size * (isFull ? 0.97 : 0.58)));
	const glyph = $derived(Math.round(size * 0.56));
	// Brand assets live under static/ so rendering a marketplace card never
	// contacts a third-party vendor. Miro stays inline below as a compact vector.
	const localLogoUrl = $derived(
		brand === 'fireflies'
			? '/capability-logos/fireflies.svg'
			: brand === 'klaviyo'
				? '/capability-logos/klaviyo.png'
				: brand === 'bidx'
					? '/capability-logos/bidx.png'
					: brand === 'home24'
						? '/capability-logos/home24.ico'
						: brand === 'xydc'
							? '/capability-logos/xydc.ico'
							: null,
	);
	const localMark = $derived(Math.round(size * 0.72));
</script>

<div
	class="grid shrink-0 place-items-center overflow-hidden rounded-[9px] border"
	style="width: {size}px; height: {size}px; border-color: var(--glass-border); background: {tileBg(
		brand,
	)};"
	aria-hidden="true"
>
	{#if localLogoUrl && !mono}
		<img src={localLogoUrl} alt="" width={localMark} height={localMark} class="object-contain" />
	{:else if brand === 'amazon' && !mono}
		<svg width={mark} height={mark} viewBox="2.167 .438 251.038 259.969" xmlns="http://www.w3.org/2000/svg">
			<path
				d="m221.503 210.324c-105.235 50.083-170.545 8.18-212.352-17.271-2.587-1.604-6.984.375-3.169 4.757 13.928 16.888 59.573 57.593 119.153 57.593 59.621 0 95.09-32.532 99.527-38.207 4.407-5.627 1.294-8.731-3.16-6.872zm29.555-16.322c-2.826-3.68-17.184-4.366-26.22-3.256-9.05 1.078-22.634 6.609-21.453 9.93.606 1.244 1.843.686 8.06.127 6.234-.622 23.698-2.826 27.337 1.931 3.656 4.79-5.57 27.608-7.255 31.288-1.628 3.68.622 4.629 3.68 2.178 3.016-2.45 8.476-8.795 12.14-17.774 3.639-9.028 5.858-21.622 3.71-24.424z"
				fill="#FF9900"
			/>
			<path
				d="m150.744 108.13c0 13.141.332 24.1-6.31 35.77-5.361 9.489-13.853 15.324-23.341 15.324-12.952 0-20.495-9.868-20.495-24.432 0-28.75 25.76-33.968 50.146-33.968zm34.015 82.216c-2.23 1.992-5.456 2.135-7.97.806-11.196-9.298-13.189-13.615-19.356-22.487-18.502 18.882-31.596 24.527-55.601 24.527-28.37 0-50.478-17.506-50.478-52.565 0-27.373 14.85-46.018 35.96-55.126 18.313-8.066 43.884-9.489 63.43-11.718v-4.365c0-8.018.616-17.506-4.08-24.432-4.128-6.215-12.003-8.777-18.93-8.777-12.856 0-24.337 6.594-27.136 20.257-.57 3.037-2.799 6.026-5.835 6.168l-32.735-3.51c-2.751-.618-5.787-2.847-5.028-7.07 7.543-39.66 43.36-51.616 75.43-51.616 16.415 0 37.858 4.365 50.81 16.795 16.415 15.323 14.849 35.77 14.849 58.02v52.565c0 15.798 6.547 22.724 12.714 31.264 2.182 3.036 2.657 6.69-.095 8.966-6.879 5.74-19.119 16.415-25.855 22.393l-.095-.095"
				fill="#fff"
			/>
		</svg>
	{:else if brand === 'asana' && !mono}
		<svg width={mark} height={mark} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
			<path
				d="M18.78 12.653c-2.882 0-5.22 2.336-5.22 5.22s2.338 5.22 5.22 5.22 5.22-2.34 5.22-5.22-2.336-5.22-5.22-5.22zm-13.56 0c-2.88 0-5.22 2.337-5.22 5.22s2.338 5.22 5.22 5.22 5.22-2.338 5.22-5.22-2.336-5.22-5.22-5.22zm12-6.525c0 2.883-2.337 5.22-5.22 5.22-2.882 0-5.22-2.337-5.22-5.22 0-2.88 2.338-5.22 5.22-5.22 2.883 0 5.22 2.34 5.22 5.22z"
				fill="#F06A6A"
			/>
		</svg>
	{:else if brand === 'otto' && !mono}
		<svg width={mark} height={mark} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
			<path
				d="M20.893 7.95c-1.195 0-2.168.37-2.855 1.132.097-.265.149-.588.156-.968h-4.191c-.914 0-1.437.402-1.796 1.437l.185-1.437H8.157c-.775 0-1.307.37-1.5 1.096-.524-.84-1.457-1.26-2.636-1.26C1.779 7.95.32 9.246.059 12.01l-.033.35c-.228 2.47 1.067 3.69 3.08 3.69 2.243 0 3.702-1.307 3.963-4.072l.033-.348c.059-.634.015-1.185-.114-1.655h1.899l-.545 4.66c-.108.925.392 1.35 1.23 1.35.512 0 .686-.034.882-.066l.675-5.944h2.21l-.544 4.66c-.11.925.392 1.35 1.23 1.35.511 0 .685-.034.881-.066l.675-5.944h1.089c.376 0 .68-.087.915-.26-.342.604-.566 1.366-.654 2.296l-.032.348c-.229 2.471 1.066 3.69 3.08 3.69 2.243 0 3.701-1.306 3.962-4.07l.033-.349c.229-2.46-1.067-3.68-3.08-3.68zM4.86 11.477l-.022.262c-.152 1.872-.762 2.449-1.513 2.449-.675 0-1.153-.457-1.055-1.676l.021-.272c.153-1.862.762-2.45 1.513-2.45.664 0 1.154.468 1.056 1.687zm16.873 0-.022.262c-.153 1.872-.762 2.449-1.513 2.449-.675 0-1.154-.457-1.056-1.676l.022-.272c.152-1.862.762-2.45 1.513-2.45.664 0 1.154.468 1.056 1.687z"
				fill="#D4021D"
			/>
		</svg>
	{:else if brand === 'kaufland' && !mono}
		<svg width={mark} height={mark} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
			<path
				fill="#fff"
				fill-rule="evenodd"
				d="M0 24h24V0H0zm23.008-.989H.989V.989h22.022zM3.773 3.776h7.651v7.65H3.773zm8.801 0v7.652l7.653-7.652zm-8.801 8.8h7.651v7.651H3.773zm8.801-.004v7.652h7.653z"
			/>
		</svg>
	{:else if brand === 'allegro' && !mono}
		<svg width={mark} height={mark} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
			<text
				x="12"
				y="18.5"
				text-anchor="middle"
				font-family="ui-sans-serif, system-ui, sans-serif"
				font-size="19"
				font-weight="700"
				fill="#fff"
			>
				a
			</text>
		</svg>
	{:else if brand === 'jtl' && !mono}
		<svg width={mark} height={mark} viewBox="0 0 76.11 56.68" xmlns="http://www.w3.org/2000/svg">
			<path
				fill="#212d36"
				d="M68.32 0H7.78C3.48 0 0 3.48 0 7.78V48.9c0 4.3 3.48 7.78 7.78 7.78h60.55c4.3 0 7.78-3.48 7.78-7.78V7.78A7.79 7.79 0 0 0 68.32 0z"
			/>
			<path
				fill="#fff"
				d="M29.49 41.11c0 3.81-.8 6.21-2.38 7.16-1.08.65-3.69.97-7.77.97-3.34 0-5.53-.52-6.5-1.54-.97-1.03-1.47-3.24-1.47-6.59V32.3h7.8v8.25c0 1.77.7 2.14 1.29 2.14.48 0 1.07-.25 1.19-1.47V9.29h7.84zm17.8-25.5h-3.92v33.47h-7.85V15.61H31.6V9.29h15.7v6.32zM62.5 49.08H49.95V9.29h7.79v33.24h4.77v6.55z"
			/>
		</svg>
	{:else if brand === 'plytix' && !mono}
		<svg width={mark} height={mark} viewBox="0 0 533.953 533.953" xmlns="http://www.w3.org/2000/svg">
			<rect x="81.963" y="81.963" width="370.027" height="370.027" rx="41.257" ry="41.257" fill="#7A52FF" />
			<path
				fill="#fff"
				d="M400.907 251.811l-49.764-65.039c-2.431-3.176-7.279-3.176-9.71-.025l-26.8 34.703c-1.658 2.146-1.633 5.124.063 7.258l24.37 30.584c3.481 4.367 3.481 11.452 0 15.819l-24.648 30.944c-1.696 2.134-1.722 5.112-.063 7.258l26.712 34.579c2.431 3.151 7.279 3.151 9.71-.025l50.131-65.523c6.431-8.4 6.431-22.135 0-30.534z"
			/>
			<path
				fill="#fff"
				d="M339.575 172.642c-.006-.006-.019-.018-.025-.024-2.622-2.956-5.548-4.692-8.691-4.692h-51.045c-1.352 0-2.716.285-4.017.854-1.251.545-2.53 1.277-3.55 2.427-27.013 30.437-142.786 185.284-142.786 185.284-2.161 2.798-1.327 6.599 1.2 8.432q0 .013.013 0c.91.681 2.046 1.09 3.335 1.102h50.7c1.505 0 3.011-.347 4.428-1.054.013.012.025 0 .038-.012.038-.013.076-.025.114-.05 1.278-.558 2.505-1.389 3.606-2.505l.051-.05c.443-.434.86-.918 1.265-1.426l138.511-179.502c2.44-3.05 5.584-4.587 8.734-4.587h1.422s-2.409-3.203-3.302-4.199z"
			/>
			<path
				fill="#fff"
				d="M340.267 357.111c-3.069 0-6.138-1.498-8.504-4.47l-18.841-24.305-34.649-44.861c-.222-.29-.547-.689-.758-.963-2.529-3.274-7.816-3.269-10.399.061l-24.245 31.262c-2.146 2.768-2.18 6.215.45 9.647l28.939 37.833c2.403 3.14 5.558 4.711 8.713 4.711 0 0 49.521 0 49.546 0 .593-.024 1.195-.157 1.775-.326 2.255-.616 4.4-2.065 6.15-4.349l3.34-4.24h-1.516z"
			/>
		</svg>
	{:else if brand === 'krea' && !mono}
		<svg width={mark} height={mark} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
			<path
				fill="#fff"
				d="M8.34 1.266c1.766-.124 3.324 1.105 3.551 2.802.216 1.612-.887 3.171-2.545 3.536-.415.092-.877.066-1.317.122a4.63 4.63 0 0 0-2.748 1.34l-.008.004-.01-.001-.006-.005-.003-.009q0-.009.005-.016a.04.04 0 0 0 .007-.022 438 438 0 0 1-.01-4.541c.003-1.68 1.33-3.086 3.085-3.21"
			/>
			<path
				fill="#fff"
				d="M8.526 15.305c-2.247-.018-3.858-2.23-3.076-4.3a3.31 3.31 0 0 1 2.757-2.11c.384-.04.845-.03 1.215-.098 1.9-.353 3.368-1.806 3.665-3.657.066-.41.031-.9.128-1.335.449-2.016 2.759-3.147 4.699-2.236 1.011.476 1.69 1.374 1.857 2.447q.051.33.034.818c-.22 5.842-5.21 10.519-11.279 10.47m2.831.93a.04.04 0 0 1-.021-.02l-.001-.006.002-.006q0-.003.003-.004l.006-.003q3.458-.792 5.992-3.185.045-.042.083.007c.27.357.554.74.78 1.106a10.6 10.6 0 0 1 1.585 4.89q.037.53.023.819c-.084 1.705-1.51 3.08-3.31 3.09-1.592.01-2.992-1.077-3.294-2.597-.072-.36-.05-.858-.11-1.238q-.282-1.755-1.715-2.84zm-3.369 6.64c-1.353-.235-2.441-1.286-2.684-2.593a5 5 0 0 1-.05-.817V15.14q0-.021.016-.007c.884.786 1.814 1.266 3.028 1.346l.326.01c1.581.051 2.92 1.087 3.229 2.592.457 2.225-1.557 4.195-3.865 3.793"
			/>
		</svg>
	{:else if brand === 'shopify' && !mono}
		<svg width={mark} height={mark} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
			<path
				fill="#fff"
				d="M15.337 23.979l7.216-1.561s-2.604-17.613-2.625-17.73c-.018-.116-.114-.192-.211-.192s-1.929-.136-1.929-.136-1.275-1.274-1.439-1.411c-.045-.037-.075-.057-.121-.074l-.914 21.104h.023zM11.71 11.305s-.81-.424-1.774-.424c-1.447 0-1.504.906-1.504 1.141 0 1.232 3.24 1.715 3.24 4.629 0 2.295-1.44 3.76-3.406 3.76-2.354 0-3.54-1.465-3.54-1.465l.646-2.086s1.245 1.066 2.28 1.066c.675 0 .975-.545.975-.932 0-1.619-2.654-1.694-2.654-4.359-.034-2.237 1.571-4.416 4.827-4.416 1.257 0 1.875.361 1.875.361l-.945 2.715-.02.01zM11.17.83c.136 0 .271.038.405.135-.984.465-2.064 1.639-2.508 3.992-.656.213-1.293.405-1.889.578C7.697 3.75 8.951.84 11.17.84V.83zm1.235 2.949v.135c-.754.232-1.583.484-2.394.736.466-1.777 1.333-2.645 2.085-2.971.193.501.309 1.176.309 2.1zm.539-2.234c.694.074 1.141.867 1.429 1.755-.349.114-.735.231-1.158.366v-.252c0-.752-.096-1.371-.271-1.871v.002zm2.992 1.289c-.02 0-.06.021-.078.021s-.289.075-.714.21c-.423-1.233-1.176-2.37-2.508-2.37h-.115C12.135.209 11.669 0 11.265 0 8.159 0 6.675 3.877 6.21 5.846c-1.194.365-2.063.636-2.16.674-.675.213-.694.232-.772.87-.075.462-1.83 14.063-1.83 14.063L15.009 24l.927-21.166z"
			/>
		</svg>
	{:else if brand === 'zendesk' && !mono}
		<svg width={mark} height={mark} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
			<path
				fill="#fff"
				d="M12.914 2.904V16.29L24 2.905H12.914zM0 2.906C0 5.966 2.483 8.45 5.543 8.45s5.542-2.484 5.543-5.544H0zm11.086 4.807L0 21.096h11.086V7.713zm7.37 7.84c-3.063 0-5.542 2.48-5.542 5.543H24c0-3.06-2.48-5.543-5.543-5.543z"
			/>
		</svg>
	{:else if brand === 'confluence' && !mono}
		<svg width={mark} height={mark} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
			<path
				fill="#fff"
				d="M.87 18.257c-.248.382-.53.875-.763 1.245a.764.764 0 0 0 .255 1.04l4.965 3.054a.764.764 0 0 0 1.058-.26c.199-.332.454-.763.733-1.221 1.967-3.247 3.945-2.853 7.508-1.146l4.957 2.337a.764.764 0 0 0 1.028-.382l2.364-5.346a.764.764 0 0 0-.382-1 599.851 599.851 0 0 1-4.965-2.361C10.911 10.97 5.224 11.185.87 18.257zM23.131 5.743c.249-.405.531-.875.764-1.25a.764.764 0 0 0-.256-1.034L18.675.404a.764.764 0 0 0-1.058.26c-.195.335-.451.763-.734 1.225-1.966 3.246-3.945 2.85-7.508 1.146L4.437.694a.764.764 0 0 0-1.027.382L1.046 6.422a.764.764 0 0 0 .382 1c1.039.49 3.105 1.467 4.965 2.361 6.698 3.246 12.392 3.029 16.738-4.04z"
			/>
		</svg>
	{:else if brand === 'figma' && !mono}
		<!-- Official multicolour Figma mark, rendered on Figma's dark canvas tile. -->
		<svg width={mark} height={mark} viewBox="0 0 38 57" xmlns="http://www.w3.org/2000/svg">
			<path fill="#1abcfe" d="M19 28.5a9.5 9.5 0 1 1 19 0 9.5 9.5 0 0 1-19 0z" />
			<path fill="#0acf83" d="M0 47.5A9.5 9.5 0 0 1 9.5 38H19v9.5a9.5 9.5 0 1 1-19 0z" />
			<path fill="#ff7262" d="M19 0v19h9.5a9.5 9.5 0 1 0 0-19H19z" />
			<path fill="#f24e1e" d="M0 9.5A9.5 9.5 0 0 0 9.5 19H19V0H9.5A9.5 9.5 0 0 0 0 9.5z" />
			<path fill="#a259ff" d="M0 28.5A9.5 9.5 0 0 0 9.5 38H19V19H9.5A9.5 9.5 0 0 0 0 28.5z" />
		</svg>
	{:else if brand === 'linear' && !mono}
		<svg width={mark} height={mark} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
			<path
				fill="#fff"
				d="M2.886 4.18A11.982 11.982 0 0 1 11.99 0C18.624 0 24 5.376 24 12.009c0 3.64-1.62 6.903-4.18 9.105L2.887 4.18ZM1.817 5.626l16.556 16.556c-.524.33-1.075.62-1.65.866L.951 7.277c.247-.575.537-1.126.866-1.65ZM.322 9.163l14.515 14.515c-.71.172-1.443.282-2.195.322L0 11.358a12 12 0 0 1 .322-2.195Zm-.17 4.862 9.823 9.824a12.02 12.02 0 0 1-9.824-9.824Z"
			/>
		</svg>
	{:else if brand === 'slack' && !mono}
		<!-- Official Slack mark (Simple Icons), rendered white on Slack aubergine. -->
		<svg width={mark} height={mark} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
			<path
				fill="#fff"
				d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"
			/>
		</svg>
	{:else if brand === 'posthog' && !mono}
		<svg width={mark} height={mark} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
			<path
				fill="#fff"
				d="M9.854 14.5 5 9.647.854 5.5A.5.5 0 0 0 0 5.854V8.44a.5.5 0 0 0 .146.353L5 13.647l.147.146L9.854 18.5l.146.147v-.049c.065.03.134.049.207.049h2.586a.5.5 0 0 0 .353-.854L9.854 14.5zm0-5-4-4a.487.487 0 0 0-.409-.144.515.515 0 0 0-.356.21.493.493 0 0 0-.089.288V8.44a.5.5 0 0 0 .147.353l9 9a.5.5 0 0 0 .853-.354v-2.585a.5.5 0 0 0-.146-.354l-5-5zm1-4a.5.5 0 0 0-.854.354V8.44a.5.5 0 0 0 .147.353l4 4a.5.5 0 0 0 .853-.354V9.854a.5.5 0 0 0-.146-.354l-4-4zm12.647 11.515a3.863 3.863 0 0 1-2.232-1.1l-4.708-4.707a.5.5 0 0 0-.854.354v6.585a.5.5 0 0 0 .5.5H23.5a.5.5 0 0 0 .5-.5v-.6c0-.276-.225-.497-.499-.532zm-5.394.032a.8.8 0 1 1 0-1.6.8.8 0 0 1 0 1.6zM.854 15.5a.5.5 0 0 0-.854.354v2.293a.5.5 0 0 0 .5.5h2.293c.222 0 .39-.135.462-.309a.493.493 0 0 0-.109-.545L.854 15.501zM5 14.647.854 10.5a.5.5 0 0 0-.854.353v2.586a.5.5 0 0 0 .146.353L4.854 18.5l.146.147h2.793a.5.5 0 0 0 .353-.854L5 14.647z"
			/>
		</svg>
	{:else if brand === 'google' && !mono}
		<!-- Official multi-colour Google "G" — renders on a white tile. -->
		<svg width={mark} height={mark} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
			<path
				fill="#4285F4"
				d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
			/>
			<path
				fill="#34A853"
				d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
			/>
			<path
				fill="#FBBC05"
				d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
			/>
			<path
				fill="#EA4335"
				d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
			/>
		</svg>
	{:else if brand === 'typeform' && !mono}
		<!-- Typeform has no clean monochrome symbol in Simple Icons (only a busy
		     wordmark), so render a tasteful lucide-style form glyph on the brand tile
		     rather than a wrong/illegible mark. -->
		<svg
			width={glyph}
			height={glyph}
			viewBox="0 0 24 24"
			fill="none"
			stroke="#fff"
			stroke-width="1.6"
			stroke-linecap="round"
			stroke-linejoin="round"
		>
			<rect x="4" y="3" width="16" height="18" rx="2" />
			<path d="M8 8h8M8 12h8M8 16h5" />
		</svg>
	{:else if brand === 'miro' && !mono}
		<!-- Official Miro mark (Simple Icons path), kept inline for crisp small sizes. -->
		<svg width={mark} height={mark} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
			<path
				fill="#fff"
				d="M17.392 0H13.9L17 4.808 10.444 0H6.949l3.102 6.3L3.494 0H0l3.05 8.131L0 24h3.494L10.05 6.985 6.949 24h3.494L17 5.494 13.899 24h3.493L24 3.672 17.392 0z"
			/>
		</svg>
	{:else}
		<svg
			width={glyph}
			height={glyph}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			stroke-width="1.6"
			stroke-linecap="round"
			stroke-linejoin="round"
			style="color: var(--accent-foreground);"
		>
			{#if brand === 'database'}
				<ellipse cx="12" cy="5" rx="8" ry="2.5" />
				<path d="M4 5v6c0 1.4 3.6 2.5 8 2.5s8-1.1 8-2.5V5" />
				<path d="M4 11v6c0 1.4 3.6 2.5 8 2.5s8-1.1 8-2.5v-6" />
			{:else if brand === 'chart'}
				<path d="M4 19h16" />
				<path d="M6 15l4-5 3 3 5-7" />
			{:else if brand === 'pulse'}
				<path d="M3 12h4l2-6 4 12 2-6h6" />
			{:else if brand === 'sparkle'}
				<path d="M12 3l1.8 4.7L18 9.5l-4.2 1.8L12 16l-1.8-4.7L6 9.5l4.2-1.8z" />
				<path d="M19 14l.7 2.1L22 17l-2.3.8L19 20l-.7-2.2L16 17l2.3-.9z" />
			{:else if brand === 'list'}
				<path d="M8 6h12M8 12h12M8 18h12" />
				<circle cx="4" cy="6" r="1" />
				<circle cx="4" cy="12" r="1" />
				<circle cx="4" cy="18" r="1" />
			{:else if brand === 'globe'}
				<circle cx="12" cy="12" r="9" />
				<path d="M3 12h18" />
				<path d="M12 3c2.6 2.4 4 5.6 4 9s-1.4 6.6-4 9c-2.6-2.4-4-5.6-4-9s1.4-6.6 4-9z" />
			{:else if brand === 'cart'}
				<path d="M6 2 4 6v13a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6l-2-4z" />
				<path d="M4 6h16" />
				<path d="M16 10a4 4 0 0 1-8 0" />
			{:else if brand === 'message'}
				<path d="M20 14a2 2 0 0 1-2 2H8l-4 4V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2z" />
				<path d="M8 9h8M8 12h5" />
			{:else if brand === 'klaviyo'}
				<!-- No clean Simple Icon for Klaviyo → a tasteful email/send glyph. -->
				<path d="M3 5h18v14H3z" />
				<path d="m3 6 9 7 9-7" />
			{:else if brand === 'fireflies'}
				<!-- No clean Simple Icon for Fireflies → a meeting-notes mic glyph. -->
				<rect x="9" y="3" width="6" height="11" rx="3" />
				<path d="M5 11a7 7 0 0 0 14 0" />
				<path d="M12 18v3M9 21h6" />
			{:else if brand === 'shapes'}
				<!-- lucide "shapes": triangle + square + circle, for the Excalidraw diagram skill. -->
				<path d="M8.3 10a.7.7 0 0 1-.626-1.079L11.4 3a.7.7 0 0 1 1.198-.043L16.3 8.9a.7.7 0 0 1-.572 1.1Z" />
				<rect x="3" y="14" width="7" height="7" rx="1" />
				<circle cx="17.5" cy="17.5" r="3.5" />
			{:else}
				<rect x="2" y="3" width="20" height="8" rx="2" />
				<rect x="2" y="13" width="20" height="8" rx="2" />
				<path d="M6 7h.01M6 17h.01" />
			{/if}
		</svg>
	{/if}
</div>
